import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import {
  cmdbAddRelation,
  cmdbPinAttribute,
  cmdbRecordQuery,
  cmdbRemoveRelation,
  cmdbSearch,
  type CiAuditRow,
  type CiEdge,
  type CiField,
  type CiImpactRow,
  type CiRecordPage as RecordPage,
  type CiTelemetry,
  type CiTicket,
  type Owner,
  type ProvenanceRow,
} from '../api/cmdb'
import { ApiError } from '../api/client'
import { moduleOf, shellQuery } from '../api/shell'
import { Forbidden } from '../ui/Forbidden'
import { newAlert, toast } from '../ui/notify'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'
import { CiFormModal } from './CiFormModal'
import { EdgeRelation } from './EdgeRelation'
import { ScanIdentityCard, ScanSection } from './ScanCards'
import { MachineLink, SoftwareProgramCard, SoftwareSection } from './SoftwareCards'

/**
 * `Cmdb::view` - fisa unui element, la /app/cmdb/view/{id}.
 *
 * Adresa e cea din portal fiindca spre ea trimit cam patruzeci de linkuri
 * scrise in alte module: tabloul ITIL si registrul de domenii din Overview,
 * schimbarile si expunerea din Softsee, problemele si elementele afectate ale
 * unui tichet din Easeedesk, catalogul de servicii si harta din Inventoree.
 *
 * Ce arata, in ordinea in care se citeste: ce e lucrul asta, cine raspunde de
 * el, cu ce e legat, ce cade daca el cade, ce tichete a avut, si cine i-a
 * schimbat ce.
 */

const STYLES = ['css/adm.css']

export function CmdbRecordPage({ id }: { id: number }) {
  const shell = useQuery(shellQuery(moduleOf('/cmdb')))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const page = useQuery(cmdbRecordQuery(id))

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (page.error instanceof ApiError && page.error.status === 404) {
    return (
      <Content>
        <div className="alert alert-warning" style={{ margin: 20 }}>
          <i className="fas fa-exclamation-circle"></i> No such configuration item, or it belongs to another customer.
        </div>
      </Content>
    )
  }
  if (page.error) {
    return (
      <Content>
        <div className="alert alert-danger" role="alert">
          Something went wrong: {page.error.message}
        </div>
      </Content>
    )
  }
  if (!page.data) return <Spinner />

  return (
    <Content>
      <Record page={page.data} />
    </Content>
  )
}

/**
 * Aceeasi fisa, ca FRAGMENT in fisa aparatului - `Cmdb::deviceView` chema
 * `renderCi(..., true)`: toate bucatile (campurile clasei, ownership,
 * provenienta, costuri, telemetrie, Edit), nu o copie mai mica.
 */
export function CmdbRecordFragment({ id }: { id: number }) {
  const page = useQuery(cmdbRecordQuery(id))
  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  return <Record page={page.data} />
}

function Record({ page }: { page: RecordPage }) {
  const ci = page.ci
  const query = useQueryClient()
  const [editing, setEditing] = useState(false)
  const reload = () => void query.invalidateQueries({ queryKey: ['cmdb', 'ci', ci.id] })

  return (
    /*
     * Fragment, nu inca un `<div id="content">`: invelisul acela il pune deja
     * `<Content>` din Shell. Doua elemente cu acelasi id nu sunt doar o greseala
     * de markup - `adm.css` deseneaza pe `#content` spalarea de fundal a
     * modulului (doua gradiente radiale), iar doua cutii una in alta o pictau de
     * doua ori, deci pagina iesea cu un gri mai apasat decat in portal.
     */
    <>
      <div className="adm-record-head">
        <h3>{ci.name}</h3>
        <span className="adm-record-sub">
          {ci.className}
          {ci.companyName !== '' && <> &middot; {ci.companyName}</>}
          {ci.refType === 1 && (
            <>
              {' '}
              &middot; device{' '}
              <a href={`/app/inventoree/device/${ci.refId}`} target="_blank" rel="noopener">
                {ci.deviceName !== '' ? ci.deviceName : `#${ci.refId}`}
              </a>
            </>
          )}
          {ci.refType === 2 && page.scan?.identity.ip && (
            <> &middot; discovered at {page.scan.identity.ip}</>
          )}
          {/* `refId` al unui program e randul de INSTALARE, nu masina: masina
              vine din cardul programului. */}
          {ci.refType === 3 && page.software?.machine && (
            <>
              {' '}
              &middot; installed on <MachineLink machine={page.software.machine} />
            </>
          )}
        </span>
      </div>

      {/* Campurile in ordinea din portal (`inventoree/cmdb/view.php`): clasa,
          tip, stare, sanatate, importanta, mediu, sursa, ultima vedere. Trei
          dintre ele lipseau cu totul de pe fisa noua, iar importanta statea
          lipita de titlu, unde se citea ca parte din nume. */}
      <div className="adm-bar">
        <div className="adm-field adm-field-wide">
          <label>Class</label>
          <div>{ci.className}</div>
        </div>
        <div className="adm-field">
          <label>Type</label>
          <div>{ci.kindLabel}</div>
        </div>
        <div className="adm-field">
          <label>Status</label>
          <div>
            <span className={`adm-pill ${ci.statusPill}`}>{ci.statusLabel}</span>
          </div>
        </div>
        <div className="adm-field">
          <label>Health</label>
          <div>
            <span className={`adm-pill ${ci.healthPill}`}>{ci.healthLabel}</span>
          </div>
        </div>
        <div className="adm-field">
          <label>Importance</label>
          <div>
            <span className={ci.importance.class}>{ci.importance.label}</span>
          </div>
        </div>
        <div className="adm-field">
          <label>Environment</label>
          <div>{ci.environment ? ci.environmentLabel : <span className="adm-muted">not known</span>}</div>
        </div>
        <div className="adm-field">
          <label>Source</label>
          <div>
            <Source ci={ci} />
          </div>
        </div>
        <div className="adm-field">
          <label>Last seen</label>
          <div className="adm-muted">{ci.lastSeen ?? '—'}</div>
        </div>

        {/* `.ci-actions` (adm.css:2188) le impinge in coltul din dreapta sus si
            le tine pe acelasi rand. Fara clasa asta, Edit statea la coada
            campurilor si se citea ca inca o valoare a fisei. */}
        <div className="ci-actions">
          {page.canWrite && (
            <button id="cmdbEdit" className="btn btn-primary btn-sm" onClick={() => setEditing(true)}>
              <i className="fa fa-pencil-alt"></i> Edit
            </button>
          )}
          <a className="btn btn-info btn-sm" href={`/app/adm/map?root=${ci.id}`} target="_blank" rel="noopener">
            <i className="fas fa-project-diagram"></i> Map
          </a>
        </div>
      </div>

      {/*
        Trei coloane, 4/5/3, ca in portal - nu doua jumatati.
        Pe doua coloane de cate 731px, un tabel cu doua valori ("Rack | A2") se
        intinde pe toata latimea si eticheta ajunge la o palma de valoarea ei,
        iar restul paginii ramane fundal gol. Latimile astea sunt cele ale
        ecranului vechi, si cardurile se umplu la fel.
      */}
      <div className="row">
        <div className="col-md-4">
          <Properties fields={page.fields} />
          {/* Un aparat descoperit n-are fisa de inventar: in locul ei, ce a
              gasit scanarea. */}
          {page.scan ? (
            <ScanIdentityCard scan={page.scan} />
          ) : page.software ? (
            /* Un program n-are serial sau garantie: in locul lor, ce stie
               inventarul agentului despre instalare. */
            <SoftwareProgramCard software={page.software} />
          ) : (
            <Asset ci={ci} />
          )}
        </div>
        <div className="col-md-5">
          <Connections page={page} onChanged={reload} />
        </div>
        <div className="col-md-3">
          {/* „Cine raspunde", „cine spune" si impactul, in ordinea intrebarilor
              pe care le pune cineva care tocmai a aflat ca lucrul asta a cazut:
              pe cine sun, pe ce ma bazez, ce mai cade odata cu el. */}
          <OwnershipCard ownership={page.ownership} />
          <ProvenanceCard id={ci.id} card={page.provenance} canWrite={page.canWrite} onChanged={reload} />
          <Impact rows={page.impact} />
        </div>
      </div>

      {/* Cat costa, dupa catalog: pe toata latimea, dupa rand, ca in portal -
          nu un card inghesuit intr-o coloana de langa altele. */}
      <CostsCard costs={page.costs} />
      <Tickets page={page} />
      {/* Dupa tichete si inainte de istoric, ca in portal: e observatie, nu
          interventie omeneasca. Cine citeste de sus in jos ajunge la ea dupa ce
          a inteles ce e elementul si cu ce e legat, si o foloseste ca sa
          verifice legaturile de sus. */}
      {/* Fara agent nu raporteaza nimeni din interior: „Observed by the relay"
          ar fi doua carduri goale. In locul lor, scanarea. Daca totusi exista
          telemetrie (un agent instalat intre timp), se arata si ea. */}
      {page.scan ? (
        <>
          <ScanSection scan={page.scan} />
          {page.telemetry.lastReport !== null && <Telemetry t={page.telemetry} />}
        </>
      ) : page.software ? (
        /* Un program n-are trafic propriu: „Observed by the relay" ar spune
           „nothing has ever been reported" despre ceva ce nu raporteaza
           niciodata. In locul lui, unde mai sta acelasi program. */
        <>
          <SoftwareSection software={page.software} />
          {page.telemetry.lastReport !== null && <Telemetry t={page.telemetry} />}
        </>
      ) : (
        <Telemetry t={page.telemetry} />
      )}
      <History rows={page.audit} />
      <div id="admWarning" className="adm-warning"></div>

      {editing && (
        <CiFormModal
          id={ci.id}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false)
            reload()
          }}
        />
      )}
    </>
  )
}

/**
 * De unde vine inregistrarea - scrisa de om, sau tinuta de descoperire.
 *
 * Conteaza: un element descoperit e intretinut de agent, iar o schimbare facuta
 * de mana pe el tine numai pana la urmatoarea raportare. Cuvintele sunt cele din
 * portal (`view.php`, campul „Source").
 */
function Source({ ci }: { ci: RecordPage['ci'] }) {
  if (ci.refType === 1) {
    return (
      <a href={`/app/inventoree/device/${ci.refId}`} target="_blank" rel="noopener">
        <i className="fas fa-desktop"></i> {ci.deviceName !== '' ? ci.deviceName : `Device #${ci.refId}`}
      </a>
    )
  }
  if (ci.refType === 2) {
    return (
      <a href="/app/adm/pending" target="_blank" rel="noopener" title="Added to the CMDB from ADM > Pending">
        <i className="fas fa-satellite-dish"></i> Network scan
      </a>
    )
  }
  if (ci.refType === 3) {
    /* `ref_type` 3 il scrie numai promovarea unui program din ADM > Software
       (`SoftwareQueue::promoteRow`). Portalul vechi il citea „Cloud" dupa
       `Ci::REF_CLOUD`, constanta pe care n-o scrie nimeni - eticheta era
       gresita pentru fiecare program din CMDB. */
    return (
      <a href="/app/adm/softwarePending" target="_blank" rel="noopener" title="Added to the CMDB from ADM > Software">
        <i className="fas fa-cube"></i> Software inventory
      </a>
    )
  }
  if (ci.refType === 0) return <>Added manually</>
  return <>Cloud</>
}

/** Proprietatile definite pe clasa - Type form builder. */
function Properties({ fields }: { fields: CiField[] }) {
  if (fields.length === 0) return null
  return (
    <div className="adm-card">
      <div className="adm-card-head">
        <h5>Properties</h5>
        <span className="adm-card-note">from the class blueprint</span>
      </div>
      <table className="table adm-list">
        <tbody>
          {fields.map((field) => (
            <tr key={field.id}>
              <td style={{ width: '40%' }}>{field.label}</td>
              <td>
                {field.value === null || field.value === '' ? (
                  <span className="adm-muted">&mdash;</span>
                ) : field.typeLabel === 'boolean' ? (
                  String(field.value) === '1' ? (
                    'Yes'
                  ) : (
                    'No'
                  )
                ) : (
                  String(field.value)
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Cine raspunde de element si cine il tine in picioare.
 *
 * Pe fisa, nu intr-o fila: e prima intrebare pe care o pune cineva care tocmai
 * a aflat ca lucrul asta a cazut.
 */
function OwnershipCard({ ownership }: { ownership: RecordPage['ownership'] }) {
  return (
    <div className="adm-card">
      <div className="adm-card-head">
        <h5>Who answers for it</h5>
      </div>
      <table className="table adm-list">
        <tbody>
          <tr>
            <td style={{ width: '40%' }}>Business owner</td>
            <td>
              <OwnerCell owner={ownership.business} />
            </td>
          </tr>
          {ownership.technical !== undefined && (
            <tr>
              <td>Technical owner</td>
              <td>
                <OwnerCell owner={ownership.technical} />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function OwnerCell({ owner }: { owner: Owner | null }) {
  if (owner === null) return <span className="adm-muted">nobody</span>
  return (
    <>
      {owner.missing ? <span className="adm-muted">#{owner.id} (unknown person)</span> : owner.name}
      {owner.inheritedFrom !== null && (
        <span className="adm-muted">
          {' '}
          &mdash; inherited from{' '}
          <a href={`/app/cmdb/view/${owner.inheritedFrom.id}`}>{owner.inheritedFrom.name}</a>
        </span>
      )}
    </>
  )
}

/** Faptele de inventar. Stau pe aparat, deci un serviciu le are goale. */
function Asset({ ci }: { ci: RecordPage['ci'] }) {
  const rows: [string, string | null][] = [
    ['Serial', ci.serial],
    ['Operating system', ci.osVersion],
    ['Last user', ci.lastUser],
    ['Bought', ci.acquisitionDate],
    ['Warranty until', ci.warrantyExpiry],
    ['Extended warranty until', ci.extendedWarrantyExpiry],
  ]
  if (rows.every(([, value]) => value === null || value === '')) return null
  return (
    <div className="adm-card">
      <div className="adm-card-head">
        <h5>Asset</h5>
        <span className="adm-card-note">from the equipment record</span>
      </div>
      <table className="table adm-list">
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label}>
              <td style={{ width: '40%' }}>{label}</td>
              <td>{value === null || value === '' ? <span className="adm-muted">&mdash;</span> : value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Legaturile, si adaugarea uneia de mana.
 *
 * `CiRegistry::link()` scria de la inceput muchii, dar nicio interfata nu-l
 * chema: se puteau naste numai din corelator, din resolver si din importul
 * Nagios. Un operator nu putea spune "aplicatia asta depinde de baza aia" -
 * adica exact ce face o banca pentru tot ce descoperirea nu atinge.
 */
function Connections({ page, onChanged }: { page: RecordPage; onChanged(): void }) {
  const edges = [...page.outgoing, ...page.incoming]
  const [term, setTerm] = useState('')
  const [hits, setHits] = useState<{ id: number; name: string; class: string }[]>([])
  const [target, setTarget] = useState(0)
  const [type, setType] = useState(page.relationTypes[0]?.id ?? 0)
  const timer = useRef<number | undefined>(undefined)

  /* Selectorul cauta abia cand are ce cauta: o lista cu tot ce e in parc nu e
     un selector. Aceeasi asteptare de 300 ms ca in `cmdb.js`. */
  useEffect(() => {
    window.clearTimeout(timer.current)
    setTarget(0)
    if (term.trim().length < 2) {
      setHits([])
      return
    }
    timer.current = window.setTimeout(() => {
      void cmdbSearch(page.ci.id, term)
        .then((result) => setHits(result.hits))
        .catch(() => setHits([]))
    }, 300)
    return () => window.clearTimeout(timer.current)
  }, [term, page.ci.id])

  const add = () => {
    if (target < 1 || type < 1) return
    void cmdbAddRelation(page.ci.id, target, type)
      .then((result) => {
        if (result.status !== 'OK') {
          newAlert('<i class="fas fa-exclamation-circle"></i> ' + result.message, 'danger', 7000, document.querySelector('#admWarning'))
          return
        }
        toast(result.message, 'CMDB')
        setTerm('')
        setHits([])
        onChanged()
      })
      .catch((error: unknown) => toast(message(error), 'CMDB', 'error'))
  }

  const drop = (relationId: number) => {
    void cmdbRemoveRelation(relationId)
      .then((result) => {
        if (result.status !== 'OK') {
          newAlert('<i class="fas fa-exclamation-circle"></i> ' + result.message, 'danger', 7000, document.querySelector('#admWarning'))
          return
        }
        toast(result.message, 'CMDB')
        onChanged()
      })
      .catch((error: unknown) => toast(message(error), 'CMDB', 'error'))
  }

  return (
    <div className="adm-card">
      <div className="adm-card-head">
        <h5>Connections</h5>
        <span className="adm-card-note">{edges.length} in total</span>
      </div>
      {edges.length === 0 ? (
        <p className="text-muted" style={{ padding: '10px 15px' }}>
          Nothing is linked to this item yet.
        </p>
      ) : (
        <table className="table adm-list">
          <tbody>
            {edges.map((edge) => (
              <Edge key={edge.direction + edge.id} edge={edge} canWrite={page.canWrite} onRemove={drop} />
            ))}
          </tbody>
        </table>
      )}
      {page.canWrite && page.relationTypes.length > 0 && (
        <div className="adm-bar" style={{ padding: '8px 15px' }}>
          <div className="adm-field">
            <label htmlFor="relType">Relation</label>
            <select className="form-control input-sm" id="relType" value={type} onChange={(e) => setType(Number(e.target.value))}>
              {page.relationTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div className="adm-field adm-field-wide">
            <label htmlFor="relSearch">Item</label>
            <input
              type="text"
              className="form-control input-sm"
              id="relSearch"
              placeholder="type at least two letters"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
            {hits.length > 0 && (
              <select
                className="form-control input-sm"
                style={{ marginTop: 4 }}
                size={Math.min(6, hits.length)}
                value={target}
                onChange={(e) => setTarget(Number(e.target.value))}
              >
                {hits.map((hit) => (
                  <option key={hit.id} value={hit.id}>
                    {hit.name} — {hit.class}
                  </option>
                ))}
              </select>
            )}
          </div>
          <button className="btn btn-sm btn-success" disabled={target < 1} onClick={add}>
            Link
          </button>
        </div>
      )}
    </div>
  )
}

function Edge({ edge, canWrite, onRemove }: { edge: CiEdge; canWrite: boolean; onRemove(id: number): void }) {
  return (
    <tr data-direction={edge.direction}>
      <td style={{ width: '30%' }} className="adm-muted">
        <EdgeRelation edge={edge} />
      </td>
      <td>
        <a href={`/app/cmdb/view/${edge.itemId}`}>{edge.itemName}</a>{' '}
        <span className="adm-muted">{edge.itemClass}</span>
      </td>
      <td className="adm-muted adm-nowrap">{edge.origin}</td>
      {canWrite && (
        <td className="text-right">
          <button className="btn btn-xs btn-danger" title="Unlink" onClick={() => onRemove(edge.id)}>
            <i className="fa fa-times"></i>
          </button>
        </td>
      )}
    </tr>
  )
}

/** Ce se strica daca acest element cade. Se citeste inainte de orice schimbare. */
function Impact({ rows }: { rows: CiImpactRow[] }) {
  return (
    <div className="adm-card">
      <div className="adm-card-head">
        <h5>Impact</h5>
        <span className="adm-card-note">what breaks if this fails</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-muted" style={{ padding: '10px 15px' }}>
          Nothing is known to depend on this item. The list is built from relations, so it is only as complete as they
          are.
        </p>
      ) : (
        <table className="table adm-list">
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <a href={`/app/cmdb/view/${row.id}`}>{row.name}</a> <span className="adm-muted">{row.className}</span>
                </td>
                <td className="adm-numeric adm-muted">{row.depth}</td>
                <td className="text-right">
                  <span className={row.importance.class}>{row.importance.label}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function Tickets({ page }: { page: RecordPage }) {
  /*
   * `.adm-section` e un FLEX (adm.css:127) - titlul si nota stau pe acelasi
   * rand. Cardul e FRATELE lui, nu copilul: pus inauntru, ar fi devenit a treia
   * coloana a randului aceluia si s-ar fi lipit langa titlu, exact cum s-a si
   * vazut la prima desenare.
   */
  return (
    <>
      <div className="adm-section">
        <h4>Tickets</h4>
        <span className="adm-section-note">
          the newest {page.ticketLimit}, {page.openTickets} still open
        </span>
      </div>
      <div className="adm-card">
        {page.tickets.length === 0 ? (
          <p className="text-muted" style={{ padding: '10px 15px' }}>
            No ticket has ever named this item.
          </p>
        ) : (
          <table className="table adm-list">
            <tbody>
              {page.tickets.map((ticket: CiTicket) => (
                <tr key={ticket.id}>
                  <td className="adm-nowrap adm-muted">{ticket.opened}</td>
                  <td>
                    <a href={`/app/easeedesk/ticket/${ticket.id}`}>{ticket.subject}</a>
                  </td>
                  <td className="text-right">
                    <span className={`adm-pill ${ticket.state.pill}`}>{ticket.state.label}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}

function History({ rows }: { rows: CiAuditRow[] }) {
  return (
    <>
      <div className="adm-section">
        <h4>History</h4>
        <span className="adm-section-note">who changed what, and when</span>
      </div>
      <div className="adm-card">
        {rows.length === 0 ? (
          <p className="text-muted" style={{ padding: '10px 15px' }}>
            Nothing has been recorded against this item yet.
          </p>
        ) : (
          <table className="table adm-list">
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  <td className="adm-nowrap adm-muted">{row.when}</td>
                  <td>{row.who}</td>
                  <td>{row.what}</td>
                  <td className="adm-muted">
                    {row.from !== '' && <>{row.from} &rarr; </>}
                    {row.to}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}

/**
 * Ce a observat releul - `row_telemetry.php`, cuvant cu cuvant.
 *
 * Doua carduri, fiindca sunt doua intrebari: „Listening on" - ce expune masina;
 * „Talks to" - cu cine vorbeste de fapt. A doua e dovada de sub Connections:
 * relatiile marcate acolo „discovered" sunt deduse din exact aceste fluxuri.
 *
 * Nicio judecata aici: care port e cautat, care adresa e „peste tot", care
 * partener e cunoscut - toate vin gata puse de server.
 */
function Telemetry({ t }: { t: CiTelemetry }) {
  const never = t.lastReport === null
  return (
    <>
      <div className="adm-section">
        <h4>Observed by the relay</h4>
        <span className="adm-section-note">
          {never ? 'nothing has ever been reported for this item' : `last report ${t.lastReport}`}
        </span>
      </div>
      <div className="row">
        <div className="col-md-5">
          <div className="adm-card adm-tone-blue-hoki" id="cmdbListeners">
            <div className="adm-card-head">
              <h5>
                <i className="fas fa-ethernet"></i> Listening on
              </h5>
              <span className="adm-card-note">
                {t.listeners.length} port{t.listeners.length === 1 ? '' : 's'}
              </span>
            </div>
            {t.listeners.length === 0 ? (
              <div className="adm-empty-inline">
                <i className="fas fa-ethernet"></i>
                {never
                  ? 'The relay has not reported for this item. It reports for machines that run the agent.'
                  : 'Nothing is listening on this item.'}
              </div>
            ) : (
              <table className="table adm-list">
                <thead>
                  <tr>
                    <th style={{ width: 82 }} className="adm-numeric">
                      Port
                    </th>
                    <th>Service</th>
                    <th>Bound to</th>
                    <th style={{ width: 120 }}>Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {t.listeners.map((row) => (
                    <tr key={row.id}>
                      <td className="adm-numeric">
                        <strong>{row.port}</strong>{' '}
                        <span className="adm-muted" style={{ fontSize: 11 }}>
                          {row.proto}
                        </span>
                      </td>
                      <td>
                        {row.service ?? '—'}{' '}
                        {row.watch !== null && (
                          <span className="adm-pill adm-pill-warn" title="Administration or sharing port - worth a second look">
                            {row.watch}
                          </span>
                        )}
                      </td>
                      <td className="adm-muted" style={{ fontSize: 12 }}>
                        {row.everywhere ? (
                          <span className="adm-pill adm-pill-warn" title="Reachable from every network this machine is on">
                            all interfaces
                          </span>
                        ) : (
                          row.address
                        )}
                      </td>
                      <td className="adm-muted" style={{ fontSize: 11.5 }}>
                        {row.seen ?? ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="col-md-7">
          <div className="adm-card adm-tone-blue-mauve" id="cmdbFlows">
            <div className="adm-card-head">
              <h5>
                <i className="fas fa-exchange-alt"></i> Talks to
              </h5>
              <span className="adm-card-note">
                {t.flowTotal} pair{t.flowTotal === 1 ? '' : 's'}
                {t.flowTotal > t.flows.length && <> &middot; showing the {t.flowLimit} most recent</>}
              </span>
            </div>
            {t.flows.length === 0 ? (
              <div className="adm-empty-inline">
                <i className="fas fa-exchange-alt"></i>
                {never ? 'No connections have been reported for this item.' : 'No connections have been observed.'}
              </div>
            ) : (
              <table className="table adm-list">
                <thead>
                  <tr>
                    <th style={{ width: 34 }}></th>
                    <th>Peer</th>
                    <th style={{ width: 96 }} className="adm-numeric">
                      Port
                    </th>
                    <th>Application</th>
                    <th style={{ width: 78 }} className="adm-numeric">
                      Seen
                    </th>
                    <th style={{ width: 118 }}>Last</th>
                  </tr>
                </thead>
                <tbody>
                  {t.flows.map((row, index) => (
                    <tr key={index}>
                      <td>
                        {/* Sageata spune directia dintr-o privire; titlul ramane
                            pentru cine citeste cu cititor de ecran. */}
                        <i
                          className={`fas fa-arrow-${row.inbound ? 'left' : 'right'}`}
                          title={row.direction}
                          style={{ color: row.inbound ? '#5fb644' : '#6ba0b8' }}
                        ></i>
                      </td>
                      <td>
                        {row.peerId !== null ? (
                          <>
                            <a href={`/app/cmdb/view/${row.peerId}`} target="_blank" rel="noopener">
                              {row.peerName}
                            </a>
                            <br />
                            <span className="adm-muted" style={{ fontSize: 11 }}>
                              {row.remoteIp}
                            </span>
                          </>
                        ) : (
                          <>
                            {row.remoteIp}
                            <br />
                            <span className="adm-muted" style={{ fontSize: 11 }}>
                              not a known item
                            </span>
                          </>
                        )}
                      </td>
                      <td className="adm-numeric">
                        {row.remotePort}{' '}
                        <span className="adm-muted" style={{ fontSize: 11 }}>
                          {row.proto}
                        </span>
                      </td>
                      <td className="adm-muted" style={{ fontSize: 12 }}>
                        {row.application ?? '—'}
                      </td>
                      <td className="adm-numeric adm-muted">{row.seen.toLocaleString('en-US')}</td>
                      <td className="adm-muted" style={{ fontSize: 11.5 }}>
                        {row.lastSeen ?? ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

function message(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

/**
 * „Who says so" - de unde vine fiecare valoare, si care din ele nu se atinge.
 *
 * DE CE EXISTA CARTONASUL
 *
 * Cine se uita la o fisa vede valorile si nu poate sti care sunt afirmatii ale
 * unui OM si care sunt ce a nimerit descoperirea. Cele doua se citesc la fel si
 * nu valoreaza la fel: intr-o banca, „cine spune ca serverul asta e critic" e o
 * intrebare cu raspuns obligatoriu.
 *
 * CUIUL SE BATE PE ATRIBUT, NU PE ELEMENT INTREG
 *
 * Un buton „nu-mi atinge elementul" ar fi fost mai simplu si mai prost:
 * descoperirea are dreptate despre adresa IP si despre ce programe sunt
 * instalate, iar oprita de tot ar lasa fisa sa imbatraneasca. Omul apara ce stie
 * el mai bine, si nimic altceva.
 *
 * Butonul se deseneaza numai cu drept de scris - dar gardul adevarat e pe
 * SERVER: un buton nedesenat n-a oprit niciodata pe nimeni sa trimita cererea.
 */
function ProvenanceCard({
  id,
  card,
  canWrite,
  onChanged,
}: {
  id: number
  card: RecordPage['provenance']
  canWrite: boolean
  onChanged(): void
}) {
  const [busy, setBusy] = useState('')

  if (!card.hasTable) {
    return (
      <div className="adm-card">
        <div className="adm-card-head">
          <h5>Who says so</h5>
        </div>
        <div className="adm-empty-inline">
          <i className="fa fa-database"></i> The provenance table is not in the database yet.
        </div>
      </div>
    )
  }

  const toggle = (row: ProvenanceRow) => {
    setBusy(row.attribute)
    void cmdbPinAttribute(id, row.attribute, !row.pinned)
      .then((result) => {
        setBusy('')
        if (result.status !== 'OK') {
          toast(result.message, 'CMDB', 'error')
          return
        }
        /* Se reincarca fisa, nu se schimba doar butonul: cand cuiul cade, randul
           poate capata sau pierde propozitia „discovery wanted X", iar aia se
           socoteste pe server comparand ce a vazut masina cu valoarea de acum. */
        onChanged()
      })
      .catch(() => {
        setBusy('')
        toast('That could not be saved.', 'CMDB', 'error')
      })
  }

  return (
    <div className="adm-card" id="cmdbProvenance">
      <div className="adm-card-head">
        <h5>Who says so</h5>
        <span className="adm-card-note">where each value comes from</span>
      </div>

      <table className="table adm-list">
        <tbody>
          {card.rows.map((row) => (
            <tr key={row.attribute}>
              <td style={{ width: '34%' }}>{row.label}</td>
              <td style={{ width: '36%' }}>
                {row.source === 0 ? (
                  <span className="adm-muted">no telling where it came from</span>
                ) : (
                  <>
                    <span style={{ color: row.human ? '#3c7d4a' : '#5a6472' }}>
                      <i className={row.human ? 'fas fa-user' : 'fas fa-robot'}></i> {row.sourceLabel}
                    </span>
                    <span className="adm-muted" style={{ marginLeft: 4 }}>
                      {row.setAt}
                    </span>
                  </>
                )}
                {row.blocked !== null && (
                  <div className="text-warning small" style={{ marginTop: 3 }}>
                    <i className="fas fa-shield-alt"></i> discovery wanted “{row.blocked}”
                  </div>
                )}
              </td>
              <td style={{ width: '30%' }} className="text-right">
                {canWrite && (
                  <button
                    type="button"
                    className={row.pinned ? 'btn btn-xs btn-warning toggle-pin' : 'btn btn-xs btn-default toggle-pin'}
                    data-attribute={row.attribute}
                    disabled={busy === row.attribute}
                    onClick={() => toggle(row)}
                    title={row.pinned ? 'Discovery will not change this value' : 'Let discovery change this value'}
                  >
                    <i className="fas fa-thumbtack"></i> {row.pinned ? 'pinned' : 'free'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Se spune ce NU se poate tine, si de ce. Un ecran care ar lasa impresia
          ca orice se poate apara ar minti exact pe cine se bazeaza pe el. */}
      <p className="adm-muted small" style={{ padding: '0 12px 10px' }}>
        {card.note}
      </p>
    </div>
  )
}

/**
 * Cat costa elementul, dupa catalog.
 *
 * DOUA FELURI DE COST, SI NU SE ADUNA: unul e ce costa serviciul pe care
 * elementul il reprezinta, celalalt cat contribuie la costul altora. Adunate ar
 * da un numar care nu inseamna nimic.
 *
 * `null` inseamna ca omul n-are dreptul catalogului - si atunci sectiunea nu se
 * deseneaza deloc, ca in portal. Nu „0 lei": „n-ai voie sa vezi" nu e „nu costa".
 */
function CostsCard({ costs }: { costs: RecordPage['costs'] }) {
  if (costs === null) return null
  if (costs.own === null && costs.part.length === 0) return null

  return (
    <div className="adm-card" id="cmdbCosts">
      <div className="adm-card-head">
        <h5>What it costs</h5>
        <span className="adm-card-note">per month, from the service catalog</span>
      </div>

      {costs.own !== null && (
        <table className="table adm-list">
          <tbody>
            <tr>
              <td style={{ width: '40%' }}>This service</td>
              <td>
                <strong id="cmdbOwnCost">{money(costs.own.amount, costs.own.currency)}</strong>
                {costs.own.templateName !== null && (
                  <span className="adm-muted" style={{ marginLeft: 6 }}>
                    {costs.own.templateName}
                  </span>
                )}
                {!costs.own.published && (
                  <span className="adm-pill" style={{ marginLeft: 6 }}>
                    not published
                  </span>
                )}
                <div className="adm-muted small" style={{ marginTop: 3 }}>
                  {costs.own.counts.ci} item(s) under it · {costs.own.counts.user} user(s) ·{' '}
                  {costs.own.counts.ticket} ticket(s) this month
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {costs.part.length > 0 && (
        <>
          <p className="adm-muted small" style={{ padding: '6px 12px 0', marginBottom: 4 }}>
            What it carries in the cost of the services it holds up:
          </p>
          <table className="table adm-list" id="cmdbPartCosts">
            <tbody>
              {costs.part.map((row) => (
                <tr key={row.serviceCiId}>
                  <td>
                    <a href={`/app/cmdb/view/${row.serviceCiId}`}>{row.serviceName}</a>
                  </td>
                  <td className="text-right">
                    {row.amount === null ? (
                      /* Serviciul n-are nicio regula pe element, deci elementul nu
                         duce nimic - iar o linie cu zero ar sugera ca duce. */
                      <span className="adm-muted">no per-item rate</span>
                    ) : (
                      money(row.amount, row.currency ?? '')
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}

/** Fiecare suma sta pe MONEDA ei: doua monede nu au un total comun. */
function money(amount: number, currency: string): string {
  return amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + currency
}
